import { useContext } from 'react'
import { oncePerSession, oncePer24Hours } from '../../helpers'
import { buildMikasaClient } from '../../helpers/api'
import { isEmpty } from 'lodash'

import { AppContext } from '../../contexts'
import { useAsync, useIsExtension } from '../utility'
import { useAuthSettings } from '../authorization'
import {
  CHROMEBOT_BOTHEAD_CLICK,
  CHROMEBOT_UPDATED_PROFILE_SETTINGS,
  CHROMEBOT_FEEDBACK,
  EXTENSION_CLIENT_EVENT_SOURCE,
  EMBED_CLIENT_EVENT_SOURCE,
} from '../../constants/clientEvents'

import { useClientEventsQuery } from './queries'

const trackEventFunc =
  (
    authSettings,
    isExtension,
    eventName,
    eventProperties = {},
    debounceFunc = () => true
  ) =>
  async uniquenessKey => {
    const client = await buildMikasaClient(authSettings)

    if (!eventName) {
      throw new Error('EventName required to track event.')
    }

    let eventKey = eventName
    if (uniquenessKey) {
      eventKey += ` + ${uniquenessKey}`
    }

    const shouldFire = await debounceFunc(eventKey)

    if (shouldFire) {
      let source = isExtension
        ? EXTENSION_CLIENT_EVENT_SOURCE
        : EMBED_CLIENT_EVENT_SOURCE
      let body = { client_event: { name: eventName, source: source } }

      if (!isEmpty(eventProperties)) {
        body = {
          client_event: { ...body.client_event, properties: eventProperties },
        }
      }

      await client.post('/chromebot/client_events', body)
    }
  }

export const useChromeBotUpdatedProfileSettingsClientEvents = () => {
  const { data, ...restProps } = useClientEventsQuery()

  let chromeBotUpdatedProfileSettingsClientEvents = []

  if (data) {
    chromeBotUpdatedProfileSettingsClientEvents = data.filter(
      clientEvent => clientEvent.name === CHROMEBOT_UPDATED_PROFILE_SETTINGS
    )
  }

  return {
    restProps,
    data: chromeBotUpdatedProfileSettingsClientEvents,
  }
}

// Hook encapsulates 'session' and 'zpid' uniqueness
// Per useAsync
const useTrackBotheadClickAsync = () => {
  const authSettings = useAuthSettings()
  const isExtension = useIsExtension()
  const trackFunction = trackEventFunc(
    authSettings,
    isExtension,
    CHROMEBOT_BOTHEAD_CLICK
  )
  const asyncFunc = async () => trackFunction()

  return useAsync(asyncFunc)
}

const useShouldShowFeedback = () => {
  const result =
    useAsync(async () => oncePer24Hours(CHROMEBOT_FEEDBACK, true), {
      executeOnMount: true,
    }) ?? {}

  return !!result?.executeValue
}

const useTrackFeedbackClick = () => {
  const { thumbDirection } = useContext(AppContext)
  const authSettings = useAuthSettings()
  const isExtension = useIsExtension()

  const trackFunction = async () =>
    trackEventFunc(
      authSettings,
      isExtension,
      CHROMEBOT_FEEDBACK,
      {
        thumb_direction: thumbDirection,
      },
      oncePer24Hours
    )
  return useAsync(trackFunction)
}

export {
  useTrackBotheadClickAsync,
  useTrackFeedbackClick,
  useShouldShowFeedback,
}
